import React from 'react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-shell via-sand-light to-fog">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 mr-3">
                <img 
                  src="/images/HLVLogo.svg" 
                  alt="Harmony Living by V Logo" 
                  className="w-full h-full"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-coastal-blue font-serif">Harmony Living by V</h1>
                <p className="text-sm text-gray-600">Terms of Service</p>
              </div>
            </div>
            <a 
              href="/"
              className="bg-coastal-blue text-white px-4 py-2 rounded-full hover:bg-coastal-dark transition-colors font-medium"
            >
              Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Terms of Service Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="prose max-w-none">
            <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">Terms of Service</h1>
            <p className="text-gray-600 mb-8 text-center">
              <strong>Effective Date:</strong> December 1, 2024 | <strong>Last Updated:</strong> January 8, 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                Welcome to Harmony Living by V. These Terms of Service ("Terms") govern your use of our website and professional organizing services. By accessing our website or engaging our services, you agree to be bound by these Terms and our Privacy Policy.
              </p>
              <p>
                If you do not agree to these Terms, please do not use our website or services. We reserve the right to modify these Terms at any time, and your continued use constitutes acceptance of any changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Description of Services</h2>
              <p className="mb-4">
                Harmony Living by V provides professional organizing and lifestyle services, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Move-In & Move-Out Support</li>
                <li>Home Transition & Estate Organization</li>
                <li>Professional home and office organization</li>
                <li>Personal Assistant Services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Service Agreement and Booking</h2>
              <p className="mb-4">
                When you book our services:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>All services require advance scheduling and confirmation</li>
                <li>Service rates and packages are subject to change</li>
                <li>Custom quotes will be provided based on specific needs</li>
                <li>A consultation may be required before beginning services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Payment Terms</h2>
              <p className="mb-4">
                Payment terms include:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Payment is due according to agreed-upon terms in your service contract</li>
                <li>We accept various payment methods as specified during booking</li>
                <li>Late payment fees may apply for overdue accounts</li>
                <li>Refunds are handled on a case-by-case basis</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Cancellation and Rescheduling</h2>
              <p className="mb-4">
                Cancellation policy:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>24-hour advance notice is required for cancellations or rescheduling</li>
                <li>Cancellations with less than 24-hour notice may incur fees</li>
                <li>No-shows will be charged the full service fee</li>
                <li>We reserve the right to reschedule due to emergencies or unforeseen circumstances</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Client Responsibilities</h2>
              <p className="mb-4">
                As a client, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Provide accurate information about your organizing needs</li>
                <li>Ensure a safe working environment</li>
                <li>Be present or designate an authorized representative during services</li>
                <li>Secure valuable items before our arrival</li>
                <li>Provide reasonable access to areas requiring organization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Confidentiality</h2>
              <p>
                We maintain strict confidentiality regarding your personal information, home, and belongings. Our team is committed to discretion and professionalism at all times.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Limitation of Liability</h2>
              <p className="mb-4">
                Harmony Living by V's liability is limited as follows:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>We carry appropriate insurance for our professional services</li>
                <li>Our liability for damages is limited to the cost of services provided</li>
                <li>We are not responsible for items that are lost, stolen, or damaged due to normal wear and tear</li>
                <li>Clients should secure valuable items and ensure adequate insurance coverage</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Intellectual Property</h2>
              <p>
                All content on our website, including text, images, logos, and organizational methods, is the property of Harmony Living by V and is protected by copyright and other intellectual property laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Termination</h2>
              <p>
                Either party may terminate services with appropriate notice. Upon termination, payment for completed services remains due, and confidentiality obligations continue.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Governing Law</h2>
              <p>
                These Terms of Service are governed by the laws of the State of California. Any disputes will be resolved in the courts of Los Angeles County, California.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibent text-gray-800 mb-4">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be posted on our website with an updated effective date. Your continued use of our services constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">14. Contact Information</h2>
              <p className="mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-sand-light p-4 rounded-lg">
                <p><strong>Harmony Living by V</strong></p>
                <p>Email: <a href="mailto:v@harmonylivingbyv.com" className="text-coastal-blue hover:underline">v@harmonylivingbyv.com</a></p>
                <p>Phone: Local: <a href="tel:+13103883970" className="text-coastal-blue hover:underline">(310) 388-3970</a> | Toll Free: <a href="tel:+18668843970" className="text-coastal-blue hover:underline">(866) 884-3970</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;